import { PlusIcon } from "@heroicons/react/24/solid";
import { useState } from "react";

import { Button, PlaceholderEditor } from "@fronterahealth/frontera-ui-components";

import {
  CreateRecommendationBankItemMutationVariables,
  useCreateRecommendationBankItemMutation,
} from "@api/graphql/types-and-hooks";
import { PlaceholderValues, RecommendationPlaceholderEditorVariant } from "@components/GoalsBank/constants";
import { renderRecommendationBankFormDialog } from "@components/RecommendationsBank/utils";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifySuccess } from "@components/notifications/notifications";

interface RecommendationBankRecommendationsAddNewFormProps {
  folderId: string;
  refetchRecommendations: () => void;
  hasRecommendations: boolean;
}

export const RecommendationBankRecommendationsAddNewForm: React.FC<
  RecommendationBankRecommendationsAddNewFormProps
> = ({ folderId, refetchRecommendations, hasRecommendations = false }) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const createRecommendation = useCreateRecommendationBankItemMutation({});
  const { formState, RegisteredFormInput, reset, onSubmit } =
    useFormUtils<CreateRecommendationBankItemMutationVariables>({
      defaultValues: { input: { recommendationContent: "" } },
      mutationFn: async (params) => {
        createRecommendation.mutateAsync(
          {
            input: {
              recommendationContent: content,
              recommendationTitle: params.input.recommendationTitle,
              recommendationCategory: params.input.recommendationCategory,
              folderId: folderId || params.input.folderId,
            },
          },
          {
            onSuccess: async () => {
              await refetchRecommendations();
              notifySuccess(`Successfully Created New Recommendation`);
              setOpen(false);
              reset();
            },
            onError: async (error: unknown) => {
              console.error(`Error when saving New Recommendation`, error);
              setOpen(true);
            },
          },
        );
      },
    });

  const dialog = renderRecommendationBankFormDialog({
    title: "Add Recommendation",
    openForm: open,
    setOpenForm: setOpen,
    onSubmit: async () => {
      await onSubmit();
    },
    children: (
      <FormContainer onSubmit={async () => {}}>
        <RegisteredFormInput
          formKey="input.recommendationTitle"
          formState={formState}
          label="Title"
          required
          className="pr-8"
        />
        <PlaceholderEditor
          title="Content"
          className="mb-6 mr-2"
          existingContent=""
          placeholder="Type / for dynamic content"
          variant={RecommendationPlaceholderEditorVariant}
          onChange={(value) => setContent(value)}
          insertValues={PlaceholderValues}
        />
      </FormContainer>
    ),
  });

  return (
    <>
      {hasRecommendations ? (
        <div
          onClick={() => setOpen(true)}
          className="group cursor-pointer content-center items-center justify-center rounded-lg border border-dashed border-limestone-200 px-6 hover:border-rio-600"
        >
          <div className="flex items-center justify-center">
            <PlusIcon className="size-6 text-rio-600 group-hover:text-rio-500" />
            <Button
              text="Add New"
              className="pl-1 text-base group-hover:text-rio-500"
              appearance="text"
              buttonAction="action"
              onClick={(e) => {
                if (e) {
                  e.stopPropagation();
                }
                setOpen(true);
              }}
            />
          </div>
          {dialog}
        </div>
      ) : (
        <>
          <Button appearance="primary" buttonAction="action" text={"+ Add New"} onClick={() => setOpen(true)} />
          {dialog}
        </>
      )}
    </>
  );
};
